import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../api/client";
import useAuth from "../../hooks/useAuth";
import "./AuditLogPage.css";

const MODULE_LABELS = {
  USER_MANAGEMENT: "User Management",
  ROLE_MANAGEMENT: "Role Management",
  WORKFLOW_MANAGEMENT: "Workflow Management",
  PROJECT_MANAGEMENT: "Project Management",
  AUTH: "Authentication",
  TENANT_SETTINGS: "Tenant Settings",
  PLATFORM: "Platform",
};

const ACTION_COLORS = {
  CREATE: "#16a34a",
  UPDATE: "#0f62c7",
  DELETE: "#dc2626",
  ASSIGN: "#7c3aed",
  STATUS_CHANGE: "#b45309",
  PASSWORD_RESET: "#b45309",
  PASSWORD_CHANGE: "#b45309",
  LOGIN: "#0f766e",
  LOGOUT: "#64748b",
  LOGIN_FAILED: "#dc2626",
};

const EMPTY_FILTERS = {
  module: "",
  action: "",
  entityType: "",
  status: "",
  search: "",
  from: "",
  to: "",
};

const PAGE_SIZE = 25;

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Audit values can be arrays (roles, permissions, stage lists) or objects, so
// both have to read sensibly inside a table cell.
function formatValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "(none)";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
}

export default function AuditLogPage({ backTo = "/dashboard", backLabel = "Dashboard" }) {
  const { user } = useAuth();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState({ modules: [], actions: [], entityTypes: [] });
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, pageSize: PAGE_SIZE };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const resp = await api.get("/audit-logs", { params });
      setLogs(resp.data?.logs || []);
      setMeta({
        total: resp.data?.total || 0,
        totalPages: resp.data?.totalPages || 1,
      });
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to load the audit trail",
      );
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    api
      .get("/audit-logs/filters")
      .then((resp) => setOptions(resp.data || {}))
      .catch(() => {});
  }, []);

  const handleFilterChange = (key, value) => {
    setExpandedId(null);
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleExport = async () => {
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const resp = await api.get("/audit-logs/export", {
        params,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to export the audit trail");
    }
  };

  // The route is admin-only, but the page guards itself too so a direct URL
  // never renders another user's audit view while the request is in flight.
  if (user && !user.isAdmin) {
    return (
      <div className="al-page">
        <div className="al-empty">Audit trail is available to admins only.</div>
      </div>
    );
  }

  return (
    <div className="al-page">
      <Link className="back-link" to={backTo}>
        ← Back to {backLabel}
      </Link>

      <div className="al-header">
        <div>
          <h1>Audit Trail</h1>
          <p>
            Every configuration change in {user?.tenantName || "your workspace"} —
            who did it, when, and exactly what changed.
          </p>
        </div>
        <button type="button" className="al-btn" onClick={handleExport}>
          Export CSV
        </button>
      </div>

      {error ? <div className="al-error">{error}</div> : null}

      <div className="al-filters">
        <label>
          Module
          <select
            value={filters.module}
            onChange={(e) => handleFilterChange("module", e.target.value)}
          >
            <option value="">All modules</option>
            {(options.modules || []).map((name) => (
              <option key={name} value={name}>
                {MODULE_LABELS[name] || name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Action
          <select
            value={filters.action}
            onChange={(e) => handleFilterChange("action", e.target.value)}
          >
            <option value="">All actions</option>
            {(options.actions || []).map((name) => (
              <option key={name} value={name}>
                {name.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Entity
          <select
            value={filters.entityType}
            onChange={(e) => handleFilterChange("entityType", e.target.value)}
          >
            <option value="">All entities</option>
            {(options.entityTypes || []).map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Result
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange("status", e.target.value)}
          >
            <option value="">All</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
          </select>
        </label>
        <label>
          From
          <input
            type="date"
            value={filters.from}
            onChange={(e) => handleFilterChange("from", e.target.value)}
          />
        </label>
        <label>
          To
          <input
            type="date"
            value={filters.to}
            onChange={(e) => handleFilterChange("to", e.target.value)}
          />
        </label>
        <label>
          Search
          <input
            type="search"
            placeholder="Person, entity or summary"
            value={filters.search}
            onChange={(e) => handleFilterChange("search", e.target.value)}
          />
        </label>
        <div className="al-filter-actions">
          <button
            type="button"
            className="al-btn secondary"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="al-meta-row">
        {loading
          ? "Loading..."
          : `${meta.total} record(s) — page ${page} of ${meta.totalPages}`}
      </div>

      <div className="al-table-wrap">
        <table className="al-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Module</th>
              <th>Action</th>
              <th>Entity</th>
              <th>What Changed</th>
              <th>Who</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {!loading && logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="al-empty">
                  No audit records match these filters.
                </td>
              </tr>
            ) : null}
            {logs.map((log) => (
              <React.Fragment key={log.id}>
                <tr>
                  <td className="al-nowrap">{formatDateTime(log.createdAt)}</td>
                  <td>
                    {MODULE_LABELS[log.module] || log.module}
                    <div className="al-sub">{log.appModule}</div>
                  </td>
                  <td>
                    <span
                      className="al-action"
                      style={{ color: ACTION_COLORS[log.action] || "#334155" }}
                    >
                      {log.action.replace(/_/g, " ")}
                    </span>
                    {log.status === "FAILURE" ? (
                      <div className="al-sub al-failed">FAILED</div>
                    ) : null}
                  </td>
                  <td>
                    {log.entityLabel || log.entityId || "—"}
                    <div className="al-sub">{log.entityType}</div>
                  </td>
                  <td>{log.summary}</td>
                  <td>
                    {log.actorName || "System"}
                    <div className="al-sub">
                      {log.actorEmail || log.actorType}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="al-btn link"
                      onClick={() =>
                        setExpandedId(expandedId === log.id ? null : log.id)
                      }
                    >
                      {expandedId === log.id ? "Hide" : "Details"}
                    </button>
                  </td>
                </tr>

                {expandedId === log.id ? (
                  <tr>
                    <td colSpan={7} className="al-detail">
                      {Array.isArray(log.changes) && log.changes.length ? (
                        <table className="al-table al-diff">
                          <thead>
                            <tr>
                              <th>Field</th>
                              <th>Before</th>
                              <th>After</th>
                            </tr>
                          </thead>
                          <tbody>
                            {log.changes.map((change) => (
                              <tr key={change.field}>
                                <td>
                                  <strong>{change.field}</strong>
                                </td>
                                <td className="al-before">
                                  <pre>{formatValue(change.before)}</pre>
                                </td>
                                <td className="al-after">
                                  <pre>{formatValue(change.after)}</pre>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="al-sub">
                          No field-level diff recorded for this event
                          {log.action === "CREATE" || log.action === "DELETE"
                            ? " — see the full snapshot below."
                            : "."}
                        </div>
                      )}

                      <div className="al-detail-meta">
                        <div>
                          <strong>Actor</strong>
                          <div>
                            {log.actorName || "System"} (
                            {log.actorEmail || "no email"}) · {log.actorType}
                          </div>
                        </div>
                        <div>
                          <strong>Request</strong>
                          <div>
                            {log.requestMethod || "-"} {log.requestPath || "-"}
                          </div>
                        </div>
                        <div>
                          <strong>IP Address</strong>
                          <div>{log.ipAddress || "-"}</div>
                        </div>
                        <div>
                          <strong>Device</strong>
                          <div>{log.userAgent || "-"}</div>
                        </div>
                        {log.errorMessage ? (
                          <div>
                            <strong>Error</strong>
                            <div className="al-failed">{log.errorMessage}</div>
                          </div>
                        ) : null}
                      </div>

                      {log.before || log.after || log.metadata ? (
                        <div className="al-snapshots">
                          {log.before ? (
                            <div>
                              <strong>Before</strong>
                              <pre>{JSON.stringify(log.before, null, 2)}</pre>
                            </div>
                          ) : null}
                          {log.after ? (
                            <div>
                              <strong>After</strong>
                              <pre>{JSON.stringify(log.after, null, 2)}</pre>
                            </div>
                          ) : null}
                          {log.metadata ? (
                            <div>
                              <strong>Context</strong>
                              <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="al-pager">
        <button
          type="button"
          className="al-btn secondary"
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={loading || page <= 1}
        >
          Previous
        </button>
        <button
          type="button"
          className="al-btn secondary"
          onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
          disabled={loading || page >= meta.totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
}
